import React, { useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  ModalText,
  ButtonsContainer,
  ConfirmButton,
  CancelButton,
} from "../ConfirmAction/ConfirmActionModal.styled";
import { Select } from "../../Select";
import { SelectOption } from "../../Select/Select";
import {
  usersService,
  UserStatus,
} from "../../../services/usersService";
import { UserRole } from "../../../constants";
import signatureImage from "../../../assets/images/signature.jpg";

interface ReassignPatientModalProps {
  suspendedProviderId: number;
  onReassign: () => void;
  onCancel: () => void;
}

const ReassignPatientModalContent: React.FC<ReassignPatientModalProps> = ({
  suspendedProviderId,
  onReassign,
  onCancel,
}) => {
  const { t } = useTranslation();

  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [providerOptions, setProviderOptions] = useState<SelectOption[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch active providers (excluding the suspended one)
  useEffect(() => {
    setLoadingProviders(true);
    usersService
      .getUsers({
        roleId: UserRole.Provider,
        status: [UserStatus.Active],
        limit: 1000,
      })
      .then(({ data }) => {
        const options = data
          .filter((u) => u.id !== suspendedProviderId)
          .map((u) => ({
            value: u.id,
            label: `${u.firstName} ${u.lastName}`.trim(),
          }));
        setProviderOptions(options);
      })
      .catch(console.error)
      .finally(() => setLoadingProviders(false));
  }, [suspendedProviderId]);

  const handleConfirm = async () => {
    if (!selectedProviderId) return;

    setIsSubmitting(true);
    try {
      await usersService.suspendProvider(suspendedProviderId, selectedProviderId);
      onReassign();
    } catch (error) {
      console.error("Error suspending provider:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentContainer>
      <Box sx={{ mb: 3 }}>
        <img src={signatureImage} alt="" width={138} height={120} />
      </Box>
      <ModalTitle>
        {t("reassignPatient.title", "Reassign Patient")}
      </ModalTitle>
      <ModalText>
        {t(
          "reassignPatient.message",
          "Each patient must be assigned to at least one provider. Select a provider from the list below.",
        )}
      </ModalText>

      <Box sx={{ width: "100%", mb: "60px" }}>
        {loadingProviders ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Select
            label={t("reassignPatient.selectProviderLabel", "Select Provider")}
            placeholder={t("reassignPatient.selectProviderLabel", "Select Provider")}
            value={selectedProviderId ?? ""}
            onChange={(val) => setSelectedProviderId(Number(val))}
            options={providerOptions}
            fullWidth
          />
        )}
      </Box>

      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("common.cancel", "Cancel")}
        </CancelButton>
        <ConfirmButton
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting || !selectedProviderId}
          color="error"
          sx={{ minWidth: "200px" }}
        >
          {isSubmitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CircularProgress size={18} color="inherit" />
              {t("reassignPatient.confirm", "Confirm Assignment")}
            </Box>
          ) : (
            t("reassignPatient.confirm", "Confirm Assignment")
          )}
        </ConfirmButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default ReassignPatientModalContent;
